import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pin, Lock, Users, Trash2, ListTodo, CheckSquare, ChevronUp, Copy, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { notesAPI } from '@/lib/api';
import MarkdownNoteEditor, { legacyToMarkdown } from '@/components/notes/MarkdownNoteEditor';

const when = (v) => {
  if (!v) return '';
  const s = String(v);
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days < 1 && d.getDate() === new Date().getDate()) return d.toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}) });
};

// A note is Markdown; its closed preview shows the words, not the syntax.
const previewLines = (body = '') => (body || '').split('\n')
  .filter((l) => l.trim() && !/^\s*\|?\s*:?-{3,}/.test(l) && !/^\s*```/.test(l))
  .map((l) => (/^\s*\|/.test(l) ? l.split('|').map((x) => x.trim()).filter(Boolean).join(' · ') : l)
    .replace(/^\s*#{1,6}\s+/, '').replace(/^\s*>\s?/, '').replace(/(\*\*|__|~~)(.+?)\1/g, '$2')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').replace(/\\([\\`*_{}\[\]()#+\-.!|~>])/g, '$1'));
const slug = (t) => (t || 'note').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'note';

// How a note's lines read when it's closed: [ ] is a box, [→] became a task, [x] is ticked.
const Line = ({ text }) => {
  const m = text.match(/^(\s*(?:[-*]\s*)?)\[(\s?|→|x|X)\]\s+(.*)$/);
  if (!m) return <div className="truncate">{text || ' '}</div>;
  const k = m[2].trim();
  return (
    <div className="flex items-center gap-1.5 truncate">
      {k === '→' ? <span className="text-[10px] font-semibold text-sky-700 shrink-0">TASK</span>
        : <span className={`w-3 h-3 rounded-sm border shrink-0 ${k ? 'bg-[#1E2A33] border-[#1E2A33]' : 'border-[#94A3B8]'}`} />}
      <span className={k.toLowerCase() === 'x' ? 'line-through text-[#94A3B8]' : ''}>{m[3]}</span>
    </div>
  );
};

const Editor = ({ note, onSaved, onClose, onDeleted, showWhere }) => {
  const [title, setTitle] = useState(note.title || '');
  // Older notes were plain text; they open as the same words in Markdown and are saved as Markdown once edited.
  const [initial] = useState(() => (note.format === 'md' ? note.body || '' : legacyToMarkdown(note.body || '')));
  const [body, setBody] = useState(initial);
  const [saving, setSaving] = useState(false);
  const md = useRef(null);
  const timer = useRef(null);
  const last = useRef({ title: note.title || '', body: initial });

  const flush = useCallback(async (t = title, b = body) => {
    clearTimeout(timer.current);
    if (t === last.current.title && b === last.current.body) return;
    last.current = { title: t, body: b };
    setSaving(true);
    try { const r = await notesAPI.update(note.id, { title: t, body: b, format: 'md' }); onSaved(r.data); } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save the note'); }
    finally { setSaving(false); }
  }, [title, body, note.id, onSaved]);

  useEffect(() => { clearTimeout(timer.current); timer.current = setTimeout(() => flush(title, body), 800); return () => clearTimeout(timer.current); }, [title, body]); // eslint-disable-line react-hooks/exhaustive-deps
  // Leaving the page mid-sentence still saves.
  const cur = useRef({ title, body });
  cur.current = { title, body };
  useEffect(() => () => {
    const { title: t, body: b } = cur.current;
    if (t !== last.current.title || b !== last.current.body) notesAPI.update(note.id, { title: t, body: b, format: 'md' }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const todos = body.split('\n').filter((l) => /^\s*(?:[-*]\s*)?\[\s?\]\s+\S/.test(l)).length;
  const addTodo = () => md.current?.addTodo();
  const copyMd = async () => {
    const text = `${title ? `# ${title}\n\n` : ''}${body}\n`;
    try { await navigator.clipboard.writeText(text); toast.success('Copied as Markdown'); } catch { toast.error('Could not copy'); }
  };
  const downloadMd = () => {
    const blob = new Blob([`${title ? `# ${title}\n\n` : ''}${body}\n`], { type: 'text/markdown;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = `${slug(title || body.split('\n')[0])}.md`;
    document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  const makeTasks = async () => {
    await flush();
    try {
      const r = await notesAPI.makeTasks(note.id);
      setBody(r.data.note.body); last.current.body = r.data.note.body; md.current?.setMarkdown(r.data.note.body); onSaved(r.data.note);
      toast.success(`${r.data.made.length} task${r.data.made.length === 1 ? '' : 's'} added${note.project_name ? ` to ${note.project_name}` : ' to My tasks'}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not make tasks'); }
  };
  const set = async (changes) => { try { const r = await notesAPI.update(note.id, changes); onSaved(r.data); } catch (e) { toast.error(e?.response?.data?.detail || 'Could not change that'); } };
  const remove = async () => {
    if (!window.confirm('Delete this note?')) return;
    try { await notesAPI.remove(note.id); onDeleted(note.id); } catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete it'); }
  };

  return (
    <div className="p-3 space-y-2">
      <input value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => flush()} placeholder="Title (optional)"
        className="w-full bg-transparent outline-none font-semibold text-[#1E2A33] placeholder:text-[#B5AC9C]" />
      <MarkdownNoteEditor ref={md} markdown={initial} onChange={setBody} onBlur={() => flush()} autoFocus={!note.title && !note.body}
        placeholder={'Write, or paste from Claude, Notion or Docs: tables and lists keep their formatting.\nUse the to-do button for items that can become tasks.'} />
      <div className="flex items-center gap-1 flex-wrap pt-1 border-t border-[#F0EBE1]">
        <Button type="button" variant="ghost" size="sm" className="h-8 text-[#5C6670]" onClick={addTodo}><CheckSquare className="w-4 h-4 mr-1" />To-do</Button>
        {todos > 0 && <Button type="button" size="sm" className="h-8 bg-[#A62B1F] hover:bg-[#8C2318] text-white" onClick={makeTasks}><ListTodo className="w-4 h-4 mr-1" />Make {todos} task{todos === 1 ? '' : 's'}</Button>}
        <Button type="button" variant="ghost" size="icon" className={`h-8 w-8 ${note.pinned ? 'text-[#A62B1F]' : 'text-[#94A3B8]'}`} title={note.pinned ? 'Unpin' : 'Pin to top'} onClick={() => set({ pinned: !note.pinned })}><Pin className="w-4 h-4" /></Button>
        {note.mine && (note.project_id || note.contact_id) && (
          <Button type="button" variant="ghost" size="sm" className="h-8 text-[#5C6670]" onClick={() => set({ private: !note.private })}>
            {note.private ? <><Lock className="w-3.5 h-3.5 mr-1" />Only you</> : <><Users className="w-3.5 h-3.5 mr-1" />Team can see</>}
          </Button>
        )}
        {showWhere && note.project_id && <Link to={`/projects/${note.project_id}`} className="text-xs text-[#5C6670] hover:text-[#A62B1F] px-2">{note.project_name}</Link>}
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-[#94A3B8] hover:text-[#1E2A33]" title="Copy as Markdown" onClick={copyMd}><Copy className="w-4 h-4" /></Button>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-[#94A3B8] hover:text-[#1E2A33]" title="Download .md" onClick={downloadMd}><Download className="w-4 h-4" /></Button>
        <span className="ml-auto text-[11px] text-[#94A3B8]">{saving ? 'Saving…' : 'Saved'}</span>
        {(note.mine) && <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-[#94A3B8] hover:text-red-600" onClick={remove} title="Delete"><Trash2 className="w-4 h-4" /></Button>}
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-[#94A3B8]" onClick={() => { flush(); onClose(); }} title="Close"><ChevronUp className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

/**
 * Notes for a project, a contact, or the signed-in person's notepad.
 *   <NotesPanel projectId=… />   <NotesPanel contactId=… />   <NotesPanel scope="private" />
 * New notes on a project/contact are shared with the team; notepad notes are private.
 */
export default function NotesPanel({ projectId, contactId, scope = 'all', q = '', showWhere = false, emptyText, header = true, title = 'Notes' }) {
  const [notes, setNotes] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const params = { scope, ...(projectId ? { project_id: projectId } : {}), ...(contactId ? { contact_id: contactId } : {}), ...(q ? { q } : {}) };
    notesAPI.list(params).then((r) => setNotes(r.data || [])).catch(() => setNotes([]));
  }, [projectId, contactId, scope, q]);

  const saved = useCallback((n) => setNotes((xs) => (xs || []).map((x) => (x.id === n.id ? n : x))), []);
  const create = async () => {
    try {
      const r = await notesAPI.create({ project_id: projectId || null, contact_id: contactId || null });
      setNotes((xs) => [r.data, ...(xs || [])]); setOpenId(r.data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start a note'); }
  };

  return (
    <div>
      {header && (
        <div className="flex items-center mb-2">
          <span className="text-sm font-semibold text-[#1E2A33] flex-1">{title}</span>
          <Button type="button" size="sm" variant="outline" className="h-8" onClick={create} data-testid="note-new"><Plus className="w-4 h-4 mr-1" />New note</Button>
        </div>
      )}
      {!header && <Button type="button" size="sm" className="mb-3 bg-[#A62B1F] hover:bg-[#8C2318] text-white" onClick={create} data-testid="note-new"><Plus className="w-4 h-4 mr-1" />New note</Button>}
      {notes === null ? <div className="text-sm text-[#94A3B8] py-2">Loading…</div> : notes.length === 0 ? (
        <div className="text-sm text-[#5C6670] py-3">{emptyText || 'No notes yet.'}</div>
      ) : (
        <div className="space-y-2">
          {notes.map((n) => (
            <div key={n.id} className={`rounded-lg border bg-white ${openId === n.id ? 'border-[#1E2A33]' : 'border-[#E3DDD1] hover:border-[#C9C0B0]'}`}>
              {openId === n.id ? (
                <Editor note={n} onSaved={saved} onClose={() => setOpenId(null)} onDeleted={(id) => { setNotes((xs) => xs.filter((x) => x.id !== id)); setOpenId(null); }} showWhere={showWhere} />
              ) : (
                <button type="button" className="w-full text-left p-3" onClick={() => setOpenId(n.id)}>
                  <div className="flex items-center gap-1.5">
                    {n.pinned && <Pin className="w-3 h-3 text-[#A62B1F] shrink-0" />}
                    <span className="font-semibold text-sm text-[#1E2A33] truncate flex-1">{n.title || previewLines(n.body)[0] || 'Untitled note'}</span>
                    {n.todos > 0 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800">{n.todos} to-do{n.todos === 1 ? '' : 's'}</span>}
                    {n.private && <Lock className="w-3 h-3 text-[#94A3B8] shrink-0" />}
                  </div>
                  <div className="text-xs text-[#5C6670] mt-1 space-y-0.5 max-h-[3.6rem] overflow-hidden">
                    {previewLines(n.body).slice(n.title ? 0 : 1, (n.title ? 0 : 1) + 3).map((l, i) => <Line key={i} text={l} />)}
                  </div>
                  <div className="text-[11px] text-[#94A3B8] mt-1.5 flex gap-2">
                    <span>{when(n.updated_at)}</span>{!n.mine && n.owner_name && <span>{n.owner_name}</span>}
                    {showWhere && n.project_name && <span>· {n.project_name}</span>}{showWhere && n.contact_name && <span>· {n.contact_name}</span>}
                  </div>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
